import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Ellipsis, Link2, Lock, Mail, UserPlus, Users, X } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import {
  branchesQuery,
  invitesQuery,
  lookupForCompany,
  membersQuery,
  orgQuery,
  photoSrc,
  rolesQuery,
  useAddMember,
  useCreateProfile,
  useRemoveMember,
  useRenewInvite,
  useRevokeInvite,
  useUpdateMember,
  useUploadPhoto,
} from '@/api/queries'
import type { Branch, Invite, IssuedInvite, Member, Role } from '@/api/types'
import { useCompanyCaller } from '@/app/company'
import { EmailLookup, type Found } from '@/app/email-lookup'
import { InvitePanel, IssuedPanel } from '@/app/invite-panel'
import { NothingForRole } from '@/app/nothing-for-role'
import { PhotoPicker, type PickedPhoto } from '@/app/photo-picker'
import { BranchPicker, RolePicker } from '@/app/pickers'
import {
  MemberPhoto,
  NoAccountBadge,
  ProfileFields,
  ProfileInvitePanel,
  StandingReadOnly,
} from '@/app/staff-profile'
import {
  assignableRoles,
  canAddStaff,
  companySections,
  memberVerdict,
  type Caller,
} from '@/domain/abilities'
import { errorText, photoErrorText } from '@/domain/errors'
import { EMPTY_INVITE, groupInvites, hasAnyInvite, type InviteDraft } from '@/domain/invites'
import {
  branchesLine,
  hasAccount,
  memberName,
  normalizeBio,
  profileChanges,
  profileProblems,
  profileRights,
  type ProfileProblems,
} from '@/domain/staff'
import { formatDate, t } from '@/i18n'
import { Button } from '@/ui/button'
import { Avatar } from '@/ui/compact-row'
import { DataList, type DataColumn } from '@/ui/data-list'
import { Field } from '@/ui/field'
import { InlineBanner } from '@/ui/inline-banner'
import { ConfirmDialog, RowMenu, RowMenuItem, SidePanel } from '@/ui/overlays'
import { EmptyState, Page, PageHeader } from '@/ui/page'
import { SectionHeader } from '@/ui/section-header'
import { NeutralSegmented } from '@/ui/segmented'
import { LoadingBlock } from '@/ui/spinner'

export const Route = createFileRoute('/_app/c/$orgId/staff')({
  component: Staff,
})

/// A person: the photo (or initials), the name, «Ви» and «Без акаунта».
const Person = ({ member, you = false }: { member: Member; you?: boolean }) => (
  <span className="flex min-w-0 items-center gap-2.5">
    <Avatar name={memberName(member)} src={photoSrc(member.photoUrl)} className="size-8 text-[11px] max-sm:size-9" />
    <span className="truncate">{memberName(member)}</span>
    {you ? (
      <span className="shrink-0 rounded-full px-1.5 py-px text-footnote font-medium text-text-2 tint-active">{t.app.you}</span>
    ) : null}
    {hasAccount(member) ? null : <NoAccountBadge />}
  </span>
)

/// Why a row cannot be opened (R2–R4), said where the eye already is — on
/// the role — as a small lock with the reason on hover and for screen
/// readers. No lock column, no chevron column. Your own row always opens:
/// your position, bio and photo are yours (SP3).
const lockReason = (caller: Caller, role: Role | null, self: boolean): string | null => {
  if (self) return null
  const verdict = memberVerdict(caller, role)
  if (verdict.allowed) return null
  return verdict.reason === 'owner_only'
    ? t.staff.ownerOnlyHint
    : verdict.reason === 'not_below_you'
      ? t.staff.notBelowYou
      : null
}

/// Name, position, role, clubs, since — real columns instead of a name and
/// a chevron on a 900 px line (Linear members, Vercel team). On a phone: the
/// photo and name, the position and clubs under it, the role on the right.
const staffColumns = (
  me: string,
  caller: Caller,
  roleOf: (member: Member) => Role | null,
  branches: readonly Branch[],
): readonly DataColumn<Member>[] => [
  {
    key: 'name',
    header: t.staff.columns.name,
    width: 'minmax(0,1.6fr)',
    cell: (member) => <Person member={member} you={member.userId === me} />,
  },
  {
    key: 'title',
    header: t.staff.columns.title,
    width: 'minmax(0,1.3fr)',
    hideBelow: 'md',
    cell: (member) => member.title || null,
  },
  {
    key: 'role',
    header: t.staff.columns.role,
    width: 'minmax(0,1fr)',
    mobile: 'trailing',
    cell: (member) => {
      const why = lockReason(caller, roleOf(member), member.userId === me)
      return (
        <span className="inline-flex min-w-0 items-center gap-1.5" title={why ?? undefined}>
          <span className="truncate">{member.roleName ?? '—'}</span>
          {why ? (
            <>
              <Lock aria-hidden="true" className="size-3 shrink-0 text-muted" />
              <span className="sr-only">{why}</span>
            </>
          ) : null}
        </span>
      )
    },
  },
  { key: 'branches', header: t.staff.columns.branches, width: 'minmax(0,1fr)', cell: (member) => branchesLine(member, branches) },
  {
    key: 'since',
    header: t.staff.columns.since,
    width: '128px',
    numeric: true,
    hideBelow: 'xl',
    mobile: 'hidden',
    cell: (member) => formatDate(member.createdAt),
  },
]

type News = Readonly<{ kind: 'done' | 'error'; message: string }>

/// Співробітники: who works here, in what role, in which clubs — with their
/// photo and position. People join with an eatlete account (found by email
/// or invited) or as a profile without one (specs/staff-profiles.md §3),
/// which can be invited later. Adding, changing and removing follow R2–R4 —
/// the buttons exist only where the server would say yes, and when it still
/// says no, it says why. Your own row opens for your profile (SP3).
function Staff() {
  const { orgId } = Route.useParams()
  const { access, caller } = useCompanyCaller(orgId)
  const allowed = companySections(caller).includes('staff')
  // Not even asked for without `staff ≥ view` — the server would refuse it.
  const members = useQuery({ ...membersQuery(orgId), enabled: allowed })
  const roles = useQuery({ ...rolesQuery(orgId), enabled: allowed })
  const branches = useQuery({ ...branchesQuery(orgId), enabled: allowed })
  const invites = useQuery({ ...invitesQuery(orgId), enabled: allowed })
  // The company's name goes into the ready-to-send invitation text.
  const org = useQuery({ ...orgQuery(orgId), enabled: allowed })
  const [adding, setAdding] = useState(false)
  const [inviting, setInviting] = useState<InviteDraft | null>(null)
  // By user id: the panel reads the person from the list, so a new photo
  // shows the moment the list is refetched.
  const [editingId, setEditingId] = useState<string | null>(null)
  const [invitingId, setInvitingId] = useState<string | null>(null)
  const [news, setNews] = useState<News | null>(null)

  if (!allowed) return <NothingForRole />

  const roleOf = (member: Member): Role | null => roles.data?.find((r) => r.id === member.role) ?? null
  const rightsOf = (member: Member) => profileRights(caller, access.userId, member, roleOf(member))
  const byId = (userId: string | null) => members.data?.find((m) => m.userId === userId) ?? null
  const loading = members.isPending || roles.isPending || branches.isPending
  const failure = members.error ?? roles.error ?? branches.error
  const editing = byId(editingId)

  return (
    <Page wide>
      <PageHeader
        title={t.staff.title}
        meta={members.data ? String(members.data.length) : undefined}
        actions={
          canAddStaff(caller) ? (
            <>
              <Button
                variant="secondary"
                icon={<Mail className="size-[18px]" strokeWidth={2.25} />}
                onClick={() => setInviting(EMPTY_INVITE)}
              >
                {t.invites.inviteByEmail}
              </Button>
              <Button
                variant="primary"
                icon={<UserPlus className="size-[18px]" strokeWidth={2.25} />}
                onClick={() => {
                  setNews(null)
                  setAdding(true)
                }}
              >
                {t.staff.add}
              </Button>
            </>
          ) : undefined
        }
      />

      {news ? <InlineBanner kind={news.kind} message={news.message} className="mb-4" /> : null}

      {loading ? (
        <LoadingBlock label={t.app.loading} />
      ) : failure ? (
        <InlineBanner
          kind="error"
          message={errorText(failure)}
          action={{
            label: t.app.retry,
            onClick: () => {
              void members.refetch()
              void roles.refetch()
              void branches.refetch()
            },
          }}
        />
      ) : members.data && members.data.length === 0 ? (
        <EmptyState icon={<Users />} title={t.staff.empty} message={t.staff.emptyMessage} />
      ) : (
        <DataList
          label={t.staff.title}
          columns={staffColumns(access.userId, caller, roleOf, branches.data ?? [])}
          rows={members.data ?? []}
          rowKey={(member) => member.id}
          rowTarget={(member) =>
            rightsOf(member).open
              ? {
                  onClick: () => {
                    setNews(null)
                    setEditingId(member.userId)
                  },
                }
              : null
          }
          rowLabel={(member) => `${t.staff.edit}: ${memberName(member)}`}
        />
      )}

      {invites.data && roles.data ? (
        <InvitesSection
          orgId={orgId}
          caller={caller}
          invites={invites.data}
          roles={roles.data}
          companyName={org.data?.name ?? ''}
        />
      ) : invites.isError ? (
        <InlineBanner
          kind="error"
          className="mt-10"
          message={errorText(invites.error)}
          action={{ label: t.app.retry, onClick: () => void invites.refetch() }}
        />
      ) : null}

      {roles.data && branches.data ? (
        <>
          <AddMemberPanel
            open={adding}
            onOpenChange={setAdding}
            orgId={orgId}
            caller={caller}
            roles={roles.data}
            branches={branches.data}
            onInvite={(draft) => {
              setAdding(false)
              setInviting(draft)
            }}
            onCreated={(member, photoError) => {
              setAdding(false)
              setNews(
                photoError
                  ? { kind: 'error', message: t.staff.createdPhotoFailed(memberName(member), photoError) }
                  : { kind: 'done', message: t.staff.created(memberName(member)) },
              )
            }}
          />
          <InvitePanel
            draft={inviting}
            onClose={() => setInviting(null)}
            orgId={orgId}
            caller={caller}
            roles={roles.data}
            branches={branches.data}
            companyName={org.data?.name ?? ''}
          />
          {editing ? (
            <EditMemberForm
              key={editing.id}
              member={editing}
              onClose={() => setEditingId(null)}
              onInvite={() => {
                setEditingId(null)
                setInvitingId(editing.userId)
              }}
              orgId={orgId}
              caller={caller}
              me={access.userId}
              role={roleOf(editing)}
              roles={roles.data}
              branches={branches.data}
            />
          ) : null}
          <ProfileInvitePanel
            member={byId(invitingId)}
            orgId={orgId}
            companyName={org.data?.name ?? ''}
            onClose={() => setInvitingId(null)}
          />
        </>
      ) : null}
    </Page>
  )
}

type PanelProps = Readonly<{
  orgId: string
  caller: Caller
  roles: readonly Role[]
  branches: readonly Branch[]
}>

type AddMode = 'account' | 'profile'

type AddErrors = Partial<Record<'person' | 'role', string | undefined>> & ProfileProblems

/// Two ways in: an eatlete account found by email (or, with no account at
/// that address, an invitation), or a profile without an account — a trainer
/// the club keeps by name, schedules and pays, who signs in nowhere yet.
function AddMemberPanel({
  open,
  onOpenChange,
  orgId,
  caller,
  roles,
  branches,
  onInvite,
  onCreated,
}: PanelProps & {
  open: boolean
  onOpenChange: (open: boolean) => void
  /// No account has the address: invite it instead, keeping what was chosen.
  onInvite: (draft: InviteDraft) => void
  /// A profile was made; `photoError` when its photo did not go up.
  onCreated: (member: Member, photoError: string | null) => void
}) {
  const add = useAddMember(orgId)
  const create = useCreateProfile(orgId)
  const upload = useUploadPhoto(orgId)
  const [mode, setMode] = useState<AddMode>('account')
  const [found, setFound] = useState<Found | null>(null)
  const [displayName, setDisplayName] = useState('')
  const [role, setRole] = useState<string | null>(null)
  const [scope, setScope] = useState<readonly string[]>([])
  const [title, setTitle] = useState('')
  const [bio, setBio] = useState('')
  const [photo, setPhoto] = useState<PickedPhoto | null>(null)
  const [errors, setErrors] = useState<AddErrors>({})

  const reset = () => {
    setMode('account')
    setFound(null)
    setDisplayName('')
    setRole(null)
    setScope([])
    setTitle('')
    setBio('')
    if (photo) URL.revokeObjectURL(photo.previewUrl)
    setPhoto(null)
    setErrors({})
    add.reset()
    create.reset()
    upload.reset()
  }

  const close = () => {
    onOpenChange(false)
    reset()
  }

  const submitAccount = () => {
    const next = {
      ...(found && !found.blocked ? {} : { person: t.staff.form.personRequired }),
      ...(role ? {} : { role: t.staff.form.roleRequired }),
    }
    setErrors(next)
    if (!found || found.blocked || !role) return
    add.mutate(
      {
        userId: found.person.id,
        role,
        ...(displayName.trim() ? { displayName: displayName.trim() } : {}),
        ...(scope.length ? { branchScope: scope } : {}),
      },
      { onSuccess: close },
    )
  }

  const submitProfile = () => {
    const next: AddErrors = {
      ...profileProblems({ title, bio, displayName }, true),
      ...(role ? {} : { role: t.staff.form.roleRequired }),
    }
    setErrors(next)
    if (Object.keys(next).length > 0 || !role) return
    const done = (member: Member, photoError: string | null) => {
      onCreated(member, photoError)
      reset()
    }
    create.mutate(
      {
        displayName: displayName.trim(),
        role,
        ...(scope.length ? { branchScope: scope } : {}),
        ...(title.trim() ? { title: title.trim() } : {}),
        ...(normalizeBio(bio) ? { bio: normalizeBio(bio) } : {}),
      },
      {
        onSuccess: (member) => {
          if (!photo) return done(member, null)
          // Second call, after the person exists: a failed upload must not
          // read as a failed creation.
          upload.mutate(
            { userId: member.userId, file: photo.file },
            {
              onSuccess: (withPhoto) => done(withPhoto, null),
              onError: (error) => done(member, photoErrorText(error)),
            },
          )
        },
      },
    )
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (mode === 'account') submitAccount()
    else submitProfile()
  }

  const busy = add.isPending || create.isPending || upload.isPending
  const failure = mode === 'account' ? (add.isError ? add.error : null) : create.isError ? create.error : null

  return (
    <SidePanel
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      title={t.staff.form.addTitle}
      onSubmit={submit}
      footer={
        <>
          <Button variant="quiet" onClick={close}>
            {t.app.cancel}
          </Button>
          <Button type="submit" variant="primary" busy={busy}>
            {busy ? t.staff.form.submitAdding : t.staff.form.submitAdd}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-7">
        <div className="flex flex-col gap-2">
          <NeutralSegmented
            label={t.staff.addMode.label}
            onSurface
            className="self-start"
            value={mode}
            options={[
              { value: 'account', label: t.staff.addMode.account },
              { value: 'profile', label: t.staff.addMode.profile },
            ]}
            onChange={(next) => {
              setMode(next)
              setErrors({})
              add.reset()
              create.reset()
            }}
          />
          {mode === 'profile' ? <p className="text-caption text-muted">{t.staff.addMode.profileExplain}</p> : null}
        </div>

        {mode === 'account' ? (
          <>
            <EmailLookup
              autoFocus
              label={t.staff.form.email}
              hint={t.staff.form.emailHint}
              found={found}
              error={errors.person}
              onFound={(next) => {
                setFound(next)
                setErrors((prev) => ({ ...prev, person: undefined }))
                add.reset()
                if (next && !displayName.trim()) setDisplayName(next.person.name)
              }}
              missing={(email) => (
                <InlineBanner
                  kind="info"
                  title={t.invites.noAccount}
                  message={t.invites.noAccountMessage}
                  action={{
                    label: t.invites.sendInvite,
                    onClick: () => {
                      const draft = { email, role, scope, displayName: displayName.trim() }
                      close()
                      onInvite(draft)
                    },
                  }}
                />
              )}
              lookup={async (email) => {
                const res = await lookupForCompany(orgId, email)
                const held = res.memberRole ? (roles.find((r) => r.id === res.memberRole)?.name ?? res.memberRole) : null
                return held
                  ? { person: res.user, note: t.lookup.alreadyMember(held), blocked: true }
                  : { person: res.user }
              }}
            />
            <Field
              label={t.staff.form.name}
              placeholder={t.staff.form.namePlaceholder}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </>
        ) : (
          <>
            <Field
              data-autofocus=""
              label={t.staff.form.name}
              placeholder={t.staff.form.namePlaceholder}
              value={displayName}
              maxLength={120}
              onChange={(e) => {
                setDisplayName(e.target.value)
                setErrors((prev) => ({ ...prev, displayName: undefined }))
              }}
              error={errors.displayName ? t.staff.profile.nameRequired : undefined}
            />
            <PhotoPicker
              name={displayName}
              picked={photo}
              onPick={(next) => {
                if (photo) URL.revokeObjectURL(photo.previewUrl)
                setPhoto(next)
              }}
              onRemove={() => {
                if (photo) URL.revokeObjectURL(photo.previewUrl)
                setPhoto(null)
              }}
            />
            <ProfileFields
              title={title}
              bio={bio}
              onTitle={(value) => {
                setTitle(value)
                setErrors((prev) => ({ ...prev, title: undefined }))
              }}
              onBio={(value) => {
                setBio(value)
                setErrors((prev) => ({ ...prev, bio: undefined }))
              }}
              problems={errors}
            />
          </>
        )}
        <RolePicker
          roles={assignableRoles(caller, roles)}
          value={role}
          onChange={(next) => {
            setRole(next)
            setErrors((prev) => ({ ...prev, role: undefined }))
          }}
          error={errors.role}
        />
        <BranchPicker branches={branches} value={scope} onChange={setScope} />
        {failure ? <InlineBanner kind="error" message={errorText(failure)} /> : null}
      </div>
    </SidePanel>
  )
}

/// A person's panel: photo, position and bio for whoever may change the
/// profile — the person themselves included (SP3) — and role, name, clubs
/// and removal only with the standing (R2–R4). A profile without an account
/// says so, and offers the invitation that hands it to a real account (§4).
function EditMemberForm({
  member,
  onClose,
  onInvite,
  orgId,
  caller,
  me,
  role: memberRole,
  roles,
  branches,
}: PanelProps & {
  member: Member
  onClose: () => void
  onInvite: () => void
  me: string
  role: Role | null
}) {
  const update = useUpdateMember(orgId)
  const remove = useRemoveMember(orgId)
  const rights = profileRights(caller, me, member, memberRole)
  const [displayName, setDisplayName] = useState(member.displayName ?? '')
  const [role, setRole] = useState<string>(member.role)
  const [title, setTitle] = useState(member.title ?? '')
  const [bio, setBio] = useState(member.bio ?? '')
  // Ids that are no longer a club of this company are dropped from the
  // start: the server refuses them (unknown_branch), and nothing here could
  // show them to untick.
  const known = member.branchScope.filter((id) => branches.some((b) => b.id === id))
  const [scope, setScope] = useState<readonly string[]>(known)
  const [problems, setProblems] = useState<ProfileProblems>({})
  const [confirming, setConfirming] = useState(false)

  const options = assignableRoles(caller, roles)
  // The current role stays visible even if, in theory, it is not offered.
  const shown = options.some((r) => r.id === member.role)
    ? options
    : [...roles.filter((r) => r.id === member.role), ...options]

  const draft = { title, bio, displayName, role, scope }
  const changes = profileChanges(member, known, draft, rights.standing)
  const unchanged = Object.keys(changes).length === 0

  const submit = (event: FormEvent) => {
    event.preventDefault()
    const next = profileProblems(draft, false)
    setProblems(next)
    if (Object.keys(next).length > 0 || unchanged) return
    update.mutate({ userId: member.userId, ...changes }, { onSuccess: onClose })
  }

  return (
    <>
      <SidePanel
        open
        onOpenChange={(open) => (open ? undefined : onClose())}
        title={memberName(member)}
        description={member.title || member.roleName || undefined}
        onSubmit={submit}
        footer={
          <>
            {rights.standing && !rights.self ? (
              <Button variant="danger" className="mr-auto -ml-3.5" onClick={() => setConfirming(true)}>
                {t.staff.remove}
              </Button>
            ) : null}
            <Button variant="quiet" onClick={onClose}>
              {t.app.cancel}
            </Button>
            <Button type="submit" variant="primary" busy={update.isPending} disabled={unchanged}>
              {update.isPending ? t.app.saving : t.app.save}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-7">
          {hasAccount(member) ? null : (
            <InlineBanner
              kind="info"
              title={t.staff.profile.noAccountTitle}
              message={t.staff.profile.noAccountMessage}
              {...(rights.invite ? { action: { label: t.staff.profile.invite, onClick: onInvite } } : {})}
            />
          )}
          {rights.self && !rights.standing ? <p className="text-callout text-text-2">{t.staff.profile.selfNote}</p> : null}
          <MemberPhoto orgId={orgId} member={member} />
          {rights.standing ? (
            <Field
              label={t.staff.form.name}
              placeholder={t.staff.form.namePlaceholder}
              value={displayName}
              maxLength={120}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          ) : null}
          <ProfileFields
            title={title}
            bio={bio}
            onTitle={(value) => {
              setTitle(value)
              setProblems((prev) => ({ ...prev, title: undefined }))
            }}
            onBio={(value) => {
              setBio(value)
              setProblems((prev) => ({ ...prev, bio: undefined }))
            }}
            problems={problems}
          />
          {rights.standing ? (
            <>
              <RolePicker roles={shown} value={role} onChange={setRole} />
              <BranchPicker branches={branches} value={scope} onChange={setScope} />
            </>
          ) : (
            <StandingReadOnly member={member} branches={branches} />
          )}
          {update.isError ? <InlineBanner kind="error" message={errorText(update.error)} /> : null}
        </div>
      </SidePanel>

      <ConfirmDialog
        open={confirming}
        onOpenChange={(open) => {
          setConfirming(open)
          if (!open) remove.reset()
        }}
        title={t.staff.removeTitle(memberName(member))}
        message={t.staff.removeMessage}
        confirmLabel={t.staff.removeConfirm}
        busy={remove.isPending}
        error={remove.isError ? <InlineBanner kind="error" message={errorText(remove.error)} /> : null}
        onConfirm={() =>
          remove.mutate(member.userId, {
            onSuccess: () => {
              setConfirming(false)
              onClose()
            },
          })
        }
      />
    </>
  )
}

/// «Запрошення»: who was invited and has not joined yet. A pending link can
/// be issued again (the old one dies — only a hash is kept, so the old link
/// cannot be shown twice) or revoked; an expired one renewed; an accepted one
/// stays a few days as news. Actions only where the server would say yes
/// (R2–R4, the same verdict as changing that person).
function InvitesSection({
  orgId,
  caller,
  invites,
  roles,
  companyName,
}: {
  orgId: string
  caller: Caller
  invites: readonly Invite[]
  roles: readonly Role[]
  companyName: string
}) {
  const renew = useRenewInvite(orgId)
  const revoke = useRevokeInvite(orgId)
  const [issued, setIssued] = useState<IssuedInvite | null>(null)
  const [revoking, setRevoking] = useState<Invite | null>(null)
  const [revokedEmail, setRevokedEmail] = useState<string | null>(null)
  const groups = groupInvites(invites)

  if (!hasAnyInvite(groups)) return null

  const roleOf = (invite: Invite): Role | null => roles.find((r) => r.id === invite.role) ?? null
  const mayTouch = (invite: Invite) => memberVerdict(caller, roleOf(invite)).allowed

  // One table, the live ones first: what still needs a person's attention
  // reads before what is history.
  const rows = [...groups.pending, ...groups.expired, ...groups.accepted]
  const columns: readonly DataColumn<Invite>[] = [
    {
      key: 'person',
      header: t.invites.columns.person,
      width: 'minmax(0,1.5fr)',
      cell: (invite) => (
        <span className="flex min-w-0 items-center gap-2.5">
          <Avatar name={invite.displayName || invite.email} className="size-7 text-[11px] max-sm:hidden" />
          <span className="flex min-w-0 flex-col leading-tight">
            <span className="truncate">{invite.displayName || invite.email}</span>
            {invite.displayName ? (
              <span className="truncate text-caption font-normal text-muted max-sm:hidden">{invite.email}</span>
            ) : null}
          </span>
        </span>
      ),
    },
    {
      key: 'role',
      header: t.invites.columns.role,
      width: 'minmax(0,1fr)',
      mobile: 'trailing',
      cell: (invite) => invite.roleName ?? '—',
    },
    {
      key: 'state',
      header: t.invites.columns.state,
      width: 'minmax(0,1.2fr)',
      cell: (invite) =>
        invite.status === 'pending' ? (
          <span className="text-text-2">{t.invites.state.pending(formatDate(invite.expiresAt))}</span>
        ) : invite.status === 'expired' ? (
          <span className="text-warning">{t.invites.state.expired(formatDate(invite.expiresAt))}</span>
        ) : (
          <span className="text-success">{t.invites.state.accepted(formatDate(invite.acceptedAt ?? invite.expiresAt))}</span>
        ),
    },
    {
      key: 'by',
      header: t.invites.columns.by,
      width: '128px',
      hideBelow: 'lg',
      mobile: 'hidden',
      cell: (invite) => invite.invitedByName ?? '—',
    },
  ]

  return (
    <section className="mt-12 flex flex-col gap-2" aria-labelledby="invites-title">
      <SectionHeader id="invites-title" title={t.invites.section} count={rows.length} />
      {renew.isError ? <InlineBanner kind="error" message={errorText(renew.error)} /> : null}
      {revokedEmail ? <InlineBanner kind="done" message={t.invites.revoked(revokedEmail)} /> : null}
      <DataList
        label={t.invites.section}
        columns={columns}
        rows={rows}
        rowKey={(invite) => invite.id}
        rowTone={(invite) => (invite.status === 'pending' ? 'normal' : 'muted')}
        rowActions={(invite) =>
          (invite.status === 'pending' || invite.status === 'expired') && mayTouch(invite) ? (
            <RowMenu label={`${t.app.more}: ${invite.email}`} trigger={<Ellipsis className="size-[18px]" />}>
              <RowMenuItem
                icon={<Link2 className="size-4" />}
                onSelect={() => {
                  setRevokedEmail(null)
                  renew.mutate(invite.id, { onSuccess: setIssued })
                }}
              >
                {t.invites.newLink}
              </RowMenuItem>
              <RowMenuItem destructive icon={<X className="size-4" />} onSelect={() => setRevoking(invite)}>
                {t.invites.revoke}
              </RowMenuItem>
            </RowMenu>
          ) : null
        }
      />
      <p className="px-4 text-caption text-muted">
        {groups.accepted.length > 0 ? `${t.invites.sectionFootnote} ${t.invites.acceptedFootnote}` : t.invites.sectionFootnote}
      </p>

      <IssuedPanel issued={issued} companyName={companyName} onClose={() => setIssued(null)} />
      <ConfirmDialog
        open={revoking !== null}
        onOpenChange={(open) => {
          if (!open) {
            setRevoking(null)
            revoke.reset()
          }
        }}
        title={t.invites.revokeTitle(revoking?.email ?? '')}
        message={t.invites.revokeMessage}
        confirmLabel={t.invites.revokeConfirm}
        busy={revoke.isPending}
        error={revoke.isError ? <InlineBanner kind="error" message={errorText(revoke.error)} /> : null}
        onConfirm={() => {
          if (!revoking) return
          const email = revoking.email
          revoke.mutate(revoking.id, {
            onSuccess: () => {
              setRevoking(null)
              setRevokedEmail(email)
            },
          })
        }}
      />
    </section>
  )
}
